import * as React from "react";
import {
    Alert, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment,
    MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TextField,
} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {toast} from "react-toastify";
import {gql} from "@apollo/client";
import {query} from "../../lib/apollo";
import Loading from "../../components/loading";
import SingleSelect from "../../components/SingleSelect";
import StripedTableRow from "../../components/StripedTableRow";
import TableFooterRow from "../../components/TableFooterRow";
import TableHeaderRow from "../../components/TableHeaderRow";
import {searchBrokersOptions} from "../campaigns/campaignsService";
import {
    deleteDemandIo,
    DemandIo,
    DemandSchool,
    fetchDemandIos,
    fetchDemandSchools,
    saveDemandIo,
} from "./supplyDemandService";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

type Option = {id: number; value: string};

type IoForm = {
    id: number | null;
    month: number;
    year: number;
    company: Option | null;
    broker: Option | null;
    school: Option | null;
    amount: string;
};

function money(amount: number) {
    return `$${Number(amount || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
}

function percent(filled: number, demand: number) {
    if (!demand) return "—";
    return `${(filled / demand * 100).toLocaleString(undefined, {maximumFractionDigits: 1})}%`;
}

function monthTimeLabel(year: number, month: number, now = new Date()) {
    const days = new Date(year, month, 0).getDate();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const start = new Date(year, month - 1, 1);
    const end = new Date(year, month, 1);
    const name = `${MONTHS[month - 1]} ${year}`;
    if (today >= end) return `${name} is complete.`;
    if (today < start) return `${name} has not started. ${days} days left.`;
    const elapsed = today.getDate();
    const daysLeft = days - elapsed;
    const through = Math.round(elapsed / days * 100);
    return (
        <>
            {name} is <Box component="span" sx={{fontWeight: 700, color: "text.primary"}}>{through}%</Box> through, with {daysLeft} day{daysLeft === 1 ? "" : "s"} left.
        </>
    );
}

function shiftMonth(year: number, month: number, delta: number) {
    const date = new Date(year, month - 1 + delta, 1);
    return {year: date.getFullYear(), month: date.getMonth() + 1};
}

export default function SupplyDemandPage() {
    const now = React.useMemo(() => new Date(), []);
    const [month, setMonth] = React.useState(now.getMonth() + 1);
    const [year, setYear] = React.useState(now.getFullYear());
    const [rows, setRows] = React.useState([] as DemandIo[]);
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [companies, setCompanies] = React.useState([] as {id: number; name: string}[]);
    const companiesRef = React.useRef(companies);
    companiesRef.current = companies;
    const [schools, setSchools] = React.useState([] as DemandSchool[]);
    const schoolsRef = React.useRef(schools);
    schoolsRef.current = schools;
    const [form, setForm] = React.useState(null as IoForm | null);
    const [pendingDelete, setPendingDelete] = React.useState(null as DemandIo | null);

    const years = React.useMemo(() => {
        const start = Math.min(2015, year);
        const end = Math.max(now.getFullYear() + 3, year);
        const list = [];
        for (let y = end; y >= start; y--) list.push(y);
        return list;
    }, [now, year]);

    const load = React.useCallback(async () => {
        setLoading(true);
        try {
            setRows(await fetchDemandIos(month, year));
        } catch (e: any) {
            toast.error(e?.message || "Unable to load demand");
        }
        setLoading(false);
    }, [month, year]);

    React.useEffect(() => {
        document.title = "Supply / Demand | DN";
    }, []);

    React.useEffect(() => {
        load();
    }, [load]);

    React.useEffect(() => {
        query(gql`query q { companies { id name active } }`, {}).then((res) => {
            setCompanies((res?.companies || []).filter((c: any) => c.active));
        }).catch(() => {});
        fetchDemandSchools().then(setSchools).catch(() => {});
    }, []);

    const searchCompanies = React.useCallback(async (search: string) => {
        const q = search.trim().toLowerCase();
        return companiesRef.current
            .filter((c) => !q || c.name.toLowerCase().includes(q) || String(c.id).includes(q))
            .slice(0, 50)
            .map((c) => ({id: c.id, value: `${c.name} (${c.id})`}));
    }, []);

    const searchSchools = React.useCallback(async (search: string) => {
        const q = search.trim().toLowerCase();
        return schoolsRef.current
            .filter((s) => !q || s.name.toLowerCase().includes(q) || String(s.schoolId).includes(q) || String(s.id).includes(q))
            .map((s) => ({id: s.id, value: `${s.name} (${s.id})`}));
    }, []);

    const openAdd = () => setForm({id: null, month, year, company: null, broker: null, school: null, amount: ""});

    const openEdit = (row: DemandIo) => setForm({
        id: row.id,
        month: row.month,
        year: row.year,
        company: {id: row.hpCompanyId, value: `${row.companyName} (${row.hpCompanyId})`},
        broker: {id: row.brokerId, value: `${row.brokerName} (${row.brokerId})`},
        school: {id: row.schoolGroupId, value: `${row.schoolGroupName} (${row.schoolGroupId})`},
        amount: Number(row.amount).toFixed(2),
    });

    const save = async () => {
        if (!form?.company || !form.broker || !form.school) {
            toast.error("Company, broker, and school are required");
            return;
        }
        const amount = Number(form.amount);
        if (form.amount.trim() === "" || !Number.isFinite(amount) || amount < 0) {
            toast.error("Enter an amount of zero or more");
            return;
        }
        setSaving(true);
        try {
            const saved = await saveDemandIo({
                id: form.id,
                month: form.month,
                year: form.year,
                hpCompanyId: Number(form.company.id),
                brokerId: Number(form.broker.id),
                schoolGroupId: Number(form.school.id),
                amount,
            });
            if (saved?.id) {
                toast.success(form.id ? "IO updated" : "IO added");
                setForm(null);
                if (form.month === month && form.year === year) await load();
            }
        } catch (e: any) {
            toast.error(e?.message || "Unable to save IO");
        }
        setSaving(false);
    };

    const confirmDelete = async () => {
        if (!pendingDelete) return;
        try {
            await deleteDemandIo(pendingDelete.id);
            toast.success("IO deleted");
            setPendingDelete(null);
            await load();
        } catch (e: any) {
            toast.error(e?.message || "Unable to delete IO");
        }
    };

    const go = (delta: number) => {
        const next = shiftMonth(year, month, delta);
        setYear(next.year);
        setMonth(next.month);
    };
    const demandTotal = rows.reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const filledTotal = rows.reduce((sum, row) => sum + Number(row.supplyAmount || 0), 0);

    return (
        <Box>
            <h2>Supply / Demand</h2>
            <Box sx={{fontWeight: 600, mb: 1}}>Demand</Box>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={2}>
                <Button variant="outlined" onClick={() => go(-1)}>Prev</Button>
                <TextField select size="small" label="Month" value={month} onChange={(e) => setMonth(Number(e.target.value))} sx={{minWidth: 140}}>
                    {MONTHS.map((name, i) => <MenuItem key={name} value={i + 1}>{name}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Year" value={year} onChange={(e) => setYear(Number(e.target.value))} sx={{minWidth: 100}}>
                    {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </TextField>
                <Button variant="outlined" onClick={() => go(1)}>Next</Button>
                <Button variant="contained" onClick={openAdd} sx={{ml: "auto"}}>Add IO</Button>
            </Box>
            <Box sx={{mb: 2, color: "text.secondary"}}>{monthTimeLabel(year, month)}</Box>

            {loading ? <Loading /> : rows.length === 0 ? (
                <Alert severity="info">No demand IOs for {MONTHS[month - 1]} {year}.</Alert>
            ) : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableHeaderRow>
                                <TableCell>Company</TableCell>
                                <TableCell>Broker</TableCell>
                                <TableCell>School</TableCell>
                                <TableCell align="right">Demand</TableCell>
                                <TableCell align="right">Filled</TableCell>
                                <TableCell align="right">% Filled</TableCell>
                                <TableCell />
                            </TableHeaderRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => (
                                <StripedTableRow key={row.id}>
                                    <TableCell>{row.companyName}</TableCell>
                                    <TableCell>{row.brokerName}</TableCell>
                                    <TableCell>{row.schoolGroupName} ({row.schoolGroupId})</TableCell>
                                    <TableCell align="right">{money(row.amount)}</TableCell>
                                    <TableCell align="right">{money(row.supplyAmount)}</TableCell>
                                    <TableCell align="right">{percent(row.supplyAmount, row.amount)}</TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" title="Edit" onClick={() => openEdit(row)}><EditIcon fontSize="small" /></IconButton>
                                        <IconButton size="small" title="Delete" onClick={() => setPendingDelete(row)}><DeleteIcon fontSize="small" /></IconButton>
                                    </TableCell>
                                </StripedTableRow>
                            ))}
                        </TableBody>
                        <TableFooter>
                            <TableFooterRow>
                                <TableCell>Total</TableCell>
                                <TableCell />
                                <TableCell />
                                <TableCell align="right">{money(demandTotal)}</TableCell>
                                <TableCell align="right">{money(filledTotal)}</TableCell>
                                <TableCell align="right">{percent(filledTotal, demandTotal)}</TableCell>
                                <TableCell />
                            </TableFooterRow>
                        </TableFooter>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={!!form} onClose={() => !saving && setForm(null)} fullWidth maxWidth="sm">
                <DialogTitle sx={{p: 0}}>
                    <Box sx={{px: 3, pt: 2, pb: 1.5, fontSize: 20}}>
                        {form?.id ? "Edit IO" : "Add IO"}
                    </Box>
                    <Box sx={{
                        px: 3, py: 1.25,
                        bgcolor: "grey.100",
                        borderTop: "1px solid",
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        fontSize: 15,
                        fontWeight: 600,
                    }}>
                        {form ? `${MONTHS[form.month - 1]} ${form.year}` : ""}
                    </Box>
                </DialogTitle>
                <DialogContent sx={{pt: "20px !important"}}>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Box>Company<br />
                            <SingleSelect searchFunction={searchCompanies} selectedItem={form?.company} setSelectedItem={(company: Option) => setForm((f) => f && ({...f, company}))} placeholder="Company" />
                        </Box>
                        <Box>Broker<br />
                            <SingleSelect searchFunction={searchBrokersOptions} selectedItem={form?.broker} setSelectedItem={(broker: Option) => setForm((f) => f && ({...f, broker}))} placeholder="Broker" />
                        </Box>
                        <Box>School<br />
                            <SingleSelect searchFunction={searchSchools} selectedItem={form?.school} setSelectedItem={(school: Option) => setForm((f) => f && ({...f, school}))} placeholder="School" />
                        </Box>
                        <TextField
                            size="small"
                            label="Amount"
                            value={form?.amount ?? ""}
                            onChange={(e) => {
                                const raw = e.target.value.replace(/[^\d.]/g, "");
                                const [whole, ...fraction] = raw.split(".");
                                const next = fraction.length ? `${whole}.${fraction.join("").slice(0, 2)}` : whole;
                                setForm((f) => f && ({...f, amount: next}));
                            }}
                            inputProps={{inputMode: "decimal"}}
                            InputProps={{startAdornment: <InputAdornment position="start">$</InputAdornment>}}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setForm(null)} disabled={saving}>Cancel</Button>
                    <Button variant="contained" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!pendingDelete} onClose={() => setPendingDelete(null)}>
                <DialogTitle>Delete IO?</DialogTitle>
                <DialogContent>
                    {pendingDelete && `${pendingDelete.companyName} / ${pendingDelete.brokerName} / ${pendingDelete.schoolGroupName}`}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPendingDelete(null)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={confirmDelete}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
